import {
  MonitorWeight as WeightIcon,
  FitnessCenter as BodyFatIcon,
  Calculate as BMIIcon,
  Spa as LeanMassIcon,
} from "@mui/icons-material";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { subDays, format, startOfMonth, subMonths } from "date-fns";
import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

import { activityApi } from "apis";
import {
  MetricValuesAggregationRequestDto,
  MetricValuesAggregationRequestDtoIntervalEnum,
  MetricValuesAggregationRequestDtoAggregateTypesEnum,
  MetricValuesAggregationRequestDtoHealthProviderCodeEnum,
  MetricValuesAggregationRequestDtoMetricCodeEnum,
} from "libs/cellera-sdk";
import { TimeRange, getDateRangeForTimeRange } from "helpers/dateRangeHelpers";
import { useCelleraTheme } from "theme/useCelleraTheme";

interface BodyCompositionChartProps {
  patientId: string;
  metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum;
  title: string;
  unit: string;
  color: string;
  selectedTimeRange: TimeRange;
}

const BodyCompositionChart: React.FC<BodyCompositionChartProps> = ({
  patientId,
  metricCode,
  title,
  unit,
  color,
  selectedTimeRange,
}) => {
  const { colors } = useCelleraTheme();

  const metricIcon = useMemo(() => {
    const iconProps = { sx: { color, fontSize: 22 } };

    switch (metricCode) {
      case MetricValuesAggregationRequestDtoMetricCodeEnum.Weight:
        return <WeightIcon {...iconProps} />;
      case MetricValuesAggregationRequestDtoMetricCodeEnum.BodyFatPercentage:
        return <BodyFatIcon {...iconProps} />;
      case MetricValuesAggregationRequestDtoMetricCodeEnum.BodyMassIndex:
        return <BMIIcon {...iconProps} />;
      case MetricValuesAggregationRequestDtoMetricCodeEnum.LeanBodyMass:
        return <LeanMassIcon {...iconProps} />;
      default:
        return <WeightIcon {...iconProps} />;
    }
  }, [metricCode, color]);

  const { startDate, endDate } = useMemo(
    () => getDateRangeForTimeRange(selectedTimeRange),
    [selectedTimeRange],
  );

  const { data: metricData, isLoading } = useQuery({
    queryKey: [
      "bodyCompositionChart",
      patientId,
      metricCode,
      selectedTimeRange,
    ],
    queryFn: async () => {
      if (!patientId) return null;
      const interval =
        selectedTimeRange === "6M" || selectedTimeRange === "1Y"
          ? MetricValuesAggregationRequestDtoIntervalEnum.Monthly
          : MetricValuesAggregationRequestDtoIntervalEnum.Daily;

      const request: MetricValuesAggregationRequestDto = {
        metricCode,
        healthProviderCode:
          MetricValuesAggregationRequestDtoHealthProviderCodeEnum.AppleHealthkit,
        interval,
        aggregateTypes: [
          MetricValuesAggregationRequestDtoAggregateTypesEnum.LatestRecord,
        ],
        startDate,
        endDate,
        orderBy: "ASC",
      };

      const response = await activityApi.getMetricValuesAggregatedByPatientId({
        patientId,
        metricValuesAggregationRequestDto: request,
      });
      return response;
    },
    enabled: !!patientId,
    refetchOnWindowFocus: false,
  });

  const chartData = useMemo(() => {
    if (!metricData?.data) return [];

    const dataMap: Record<string, number> = {};
    for (const d of metricData.data) {
      const key = format(new Date(d.startDate), "yyyy-MM-dd");
      dataMap[key] = d.valueIntData?.latest ?? d.valueFloatData?.latest ?? 0;
    }

    const today = new Date();

    if (selectedTimeRange === "1D") {
      const key = format(today, "yyyy-MM-dd");
      const value = dataMap[key] ?? 0;
      return [
        {
          date: format(today, "MMM d, yyyy"),
          value,
        },
      ];
    }

    if (selectedTimeRange === "1W") {
      const days = [];
      for (let i = 6; i >= 0; i--) {
        const date = subDays(today, i);
        const key = format(date, "yyyy-MM-dd");
        days.push({
          date: format(date, "EEE").slice(0, 2),
          value: dataMap[key] ?? 0,
        });
      }
      return days;
    }

    if (selectedTimeRange === "1M") {
      const days = [];
      for (let i = 30; i >= 0; i--) {
        const date = subDays(today, i);
        const key = format(date, "yyyy-MM-dd");
        days.push({
          date: format(date, "dd"),
          value: dataMap[key] ?? 0,
        });
      }
      return days;
    }

    if (selectedTimeRange === "6M") {
      const months = [];
      for (let i = 5; i >= 0; i--) {
        const date = subMonths(today, i);
        const key = format(startOfMonth(date), "yyyy-MM");
        const total = Object.keys(dataMap)
          .filter((d) => d.startsWith(key))
          .reduce((sum, k) => sum + (dataMap[k] ?? 0), 0);
        const count = Object.keys(dataMap).filter((d) =>
          d.startsWith(key),
        ).length;
        months.push({
          date: format(date, "MMM"),
          value: count > 0 ? total / count : 0,
        });
      }
      return months;
    }

    if (selectedTimeRange === "1Y") {
      const months = [];
      for (let i = 11; i >= 0; i--) {
        const date = subMonths(today, i);
        const key = format(startOfMonth(date), "yyyy-MM");
        const total = Object.keys(dataMap)
          .filter((d) => d.startsWith(key))
          .reduce((sum, k) => sum + (dataMap[k] ?? 0), 0);
        const count = Object.keys(dataMap).filter((d) =>
          d.startsWith(key),
        ).length;
        months.push({
          date: format(date, "MMM").charAt(0),
          value: count > 0 ? total / count : 0,
        });
      }
      return months;
    }

    return [];
  }, [metricData, selectedTimeRange]);

  if (isLoading) {
    return (
      <Card
        sx={{
          borderRadius: 1,
          border: `1px solid ${colors.text.border}`,
          p: 2,
          height: "100%",
        }}
      >
        <Typography variant="body2">Loading {title} data...</Typography>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: 1,
        border: `1px solid ${colors.text.border}`,
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        sx={{ p: 0, flex: 1, display: "flex", flexDirection: "column", pb: 0 }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          {metricIcon}
          <Typography
            variant="body1"
            fontWeight={500}
            color="text.primary"
            sx={{ fontSize: "0.875rem" }}
          >
            {title}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, width: "100%" }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid
                stroke={colors.text.border}
                strokeDasharray="1 1"
              />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                interval={selectedTimeRange === "1M" ? 6 : 0}
                style={{ fontSize: "0.75rem" }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                style={{ fontSize: "0.75rem" }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: colors.background.paper,
                  borderColor: colors.text.border,
                  borderRadius: "8px",
                }}
                formatter={(value: number) => [
                  `${value.toFixed(1)} ${unit}`,
                  title,
                ]}
              />
              <Line
                type="monotone"
                dataKey="value"
                name={title}
                stroke={color}
                strokeWidth={2}
                dot={{ fill: color, strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, fill: color }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BodyCompositionChart;
